import {
  AddCircleOutlineRounded as AddCircleOutlineRoundedIcon,
  DeleteForeverRounded as DeleteForeverRoundedIcon,
  DeleteRounded as DeleteRoundedIcon,
  UploadRounded as UploadRoundedIcon
} from '@mui/icons-material'
import { Box, Divider, IconButton, Pagination, PaginationItem, Skeleton, Stack, Typography } from '@mui/material'
import { t } from 'i18next'
import Joi from 'joi'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import useFieldValidation from '../../../helpers/fieldValidation'
import Can from '../../Layout/Can/Can.jsx'
import Count from '../../Shared/Components/Count.jsx'
import { useAuth } from '../../Shared/Contexts/AuthContext.jsx'
import { useGlobalContext } from '../../Shared/Contexts/GlobalContext.jsx'
import ConfirmWithCheckDialog from '../../Shared/Dialogs/ConfirmWithCheckDialog'
import AlarmedFilter from '../../Shared/Filters/Fields/AlarmedFilter.jsx'
import DeviceCard from '../Card/DeviceCard.jsx'
import { useDevices } from '../Contexts/DevicesContext'
import Actions from '../Dialogs/Actions.jsx'
import AddDevice from '../Dialogs/AddDevice.jsx'
import ImportDevices from '../Dialogs/ImportDevices'
import SendAction from '../Dialogs/SendAction.jsx'
import UpdateDevice from '../Dialogs/UpdateDevice.jsx'
import DeviceFilters from '../Filters/DevicesFilters.jsx'

const DEFAULT_PAGE_SIZE = 7

const filtersSchema = Joi.object({
  filters: Joi.object({
    allowMissingPosition: Joi.boolean(),
    showHidden: Joi.boolean(),
    typeIds: Joi.array().items(Joi.number())
  }).required(),
  currentPage: Joi.number().required()
})

const FilteredPublicList = () => {
  const { currentUser: { isSuperAdmin } } = useAuth()
  const { useFetchDevices, useDeleteDevice, useHardDeleteDevice } = useDevices()
  const { devicesFilters: filters, setDevicesFilters } = useGlobalContext()
  const { openErrorAlert, openSuccessAlert } = useGlobalContext()

  const [selectedDevice, setSelectedDevice] = useState(null)
  const [isAddDeviceDialogOpen, setIsAddDeviceDialogOpen] = useState(false)
  const [isImportDeviceDialogOpen, setIsImportDeviceDialogOpen] = useState(false)
  const [isEditDeviceDialogOpen, setIsEditDeviceDialogOpen] = useState(false)
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false)
  const [isSendActionOpen, setIsSendActionOpen] = useState(false)
  const [isConfirmWithCheckOpen, setIsConfirmWithCheckOpen] = useState(false)
  const [isHardDeleteConfirmOpen, setIsHardDeleteConfirmOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [devices, setDevices] = useState()
  const [isDevicesError, setIsDevicesError] = useState(false)
  const [isDevicesLoading, setIsDevicesLoading] = useState(false)

  function base64ToJson(base64) {
    try {
      const binString = atob(base64)
      const byteString = Uint8Array.from(binString, (m) => m.codePointAt(0))
      const plainTextString = new TextDecoder().decode(byteString)
      return JSON.parse(plainTextString)
    } catch {
      return null
    }
  }

  function validateFilters(jsonPayload) {
    const isValid = useFieldValidation(jsonPayload, filtersSchema)

    if (isValid) {
      return jsonPayload
    }

    return null
  }

  const [searchParams] = useSearchParams()

  const filtersFromURL = validateFilters(base64ToJson(searchParams.get('initialFilters')))

  useEffect(() => {
    setPage(1)
  }, [filters])

  const handleDevicesFiltersChange = (filters) => {
    setDevicesFilters(filters)
  }

  const handleChange = (_, value) => setPage(value)

  const handleDelete = (device) => {
    setSelectedDevice(device)
    setIsConfirmWithCheckOpen(true)
  }

  const handleEditDevice = (device) => {
    setSelectedDevice(device)
    setIsEditDeviceDialogOpen(true)
  }

  const handleSetIsOpenEditDevice = (isOpen) => {
    setIsEditDeviceDialogOpen(isOpen)
    if (!isOpen) {
      setSelectedDevice(null)
    }
  }

  const handleAction = (device) => {
    setIsActionDialogOpen(true)
    setSelectedDevice(device)
  }

  const handleSetIsOpenActionDialogOpen = (isOpen) => {
    setIsActionDialogOpen(isOpen)
    if (!isOpen) {
      setSelectedDevice(null)
    }
  }

  const handleSendAction = (device) => {
    setIsActionDialogOpen(false)
    setIsSendActionOpen(true)
    setSelectedDevice(device)
  }

  const handleSendActionIsOpen = (isOpen) => {
    setIsSendActionOpen(isOpen)
    if (!isOpen) {
      setSelectedDevice(null)
    }
  }

  const handleCloseConfigWithCheck = () => setIsConfirmWithCheckOpen(false)

  const handleHardDelete = (device) => {
    setSelectedDevice(device)
    setIsHardDeleteConfirmOpen(true)
  }

  const handleConfirmHardDelete = () => {
    useHardDeleteDevice({ id: selectedDevice.id })
      .then(() => {
        setPage(1)
        openSuccessAlert()
        fetchDevices()
      })
      .catch((err) => openErrorAlert({ keys: err?.data?.keys || ['general'] }))
    setIsHardDeleteConfirmOpen(false)
  }

  const handleConfirmAction = () => {
    useDeleteDevice({ id: selectedDevice.id })
      .then(() => {
        setPage(1)
        openSuccessAlert()
        fetchDevices()
      })
      .catch((err) => openErrorAlert({ keys: err.data }))
    setIsConfirmWithCheckOpen(false)
  }

  const fetchDevices = async () => {
    useFetchDevices({
      filters: filtersFromURL?.filters || filters,
      page,
      pageSize: DEFAULT_PAGE_SIZE
    })
      .then((res) => {
        setDevices(res.data)
      })
      .catch(() => {
        setIsDevicesError(true)
      })
      .finally(() => {
        setIsDevicesLoading(false)
      })
  }

  useEffect(() => {
    setIsDevicesLoading(true)
    fetchDevices()
  }, [])

  const handleCloseAddDevice = ({ success }) => {
    if (success) {
      fetchDevices()
    }
    setIsAddDeviceDialogOpen(false)
  }

  const handleAlarmedFilterChange = (value) => {
    setDevicesFilters({
      ...filters,
      ...value
    })
  }

  return (
    <>
      <Stack direction="row">
        <Box sx={{ flexGrow: 1 }} />

        <Can action="devices_c">
          <IconButton
            color="primary"
            onClick={() => setIsAddDeviceDialogOpen(true)}>
            <AddCircleOutlineRoundedIcon />
          </IconButton>
          <IconButton
            color="primary"
            aria-label={`${t('devices.import')}`}
            onClick={() => setIsImportDeviceDialogOpen(true)}>
            <UploadRoundedIcon />
          </IconButton>
        </Can>
      </Stack>

      <Divider />

      <Box>
        <Typography variant="h5">
          <Count itemsCount={devices?.count} />
        </Typography>
      </Box>

      <Can action="alarms_r">
        <Divider />

        <Box py={2}>
          <AlarmedFilter
            filters={filters}
            onChange={handleAlarmedFilterChange}
          />
        </Box>

        <Divider />
      </Can>

      <Stack
        direction="row"
        spacing={1}
        my={4}>
        <DeviceFilters
          filters={filters}
          onChange={handleDevicesFiltersChange}
        />
      </Stack>

      {isDevicesLoading && (
        <Stack
          spacing={2}
          sx={{ marginBottom: '20px' }}>
          {[...Array(DEFAULT_PAGE_SIZE).keys()].map((index) => (
            <Skeleton
              key={`skeleton-${index}`}
              variant="rounded"
              sx={{ height: '230px' }}></Skeleton>
          ))}
        </Stack>
      )}

      {!isDevicesLoading &&
        !isDevicesError &&
        devices?.data.map((device, i) => (
          <DeviceCard
            key={`device-${i}`}
            device={device}
            onDelete={() => handleDelete(device)}
            onHardDelete={() => handleHardDelete(device)}
            onEdit={handleEditDevice}
            onAction={handleAction}
          />
        ))}

      <Stack
        spacing={2}
        justifyContent="space-between"
        alignItems="center">
        {!isDevicesLoading && !!devices?.count && (
          <Pagination
            count={Math.ceil(devices.count / DEFAULT_PAGE_SIZE)}
            page={page}
            onChange={handleChange}
            renderItem={(item) => {
              const ariaLabels = {
                first: t('pagination.firstPage'),
                last: t('pagination.lastPage'),
                next: t('pagination.nextPage'),
                previous: t('pagination.previousPage'),
                page: t('pagination.goToPage', { page: item.page })
              }

              return (
                <PaginationItem
                  {...item}
                  aria-label={ariaLabels[item.type]}
                />
              )
            }}
            showFirstButton
            showLastButton
          />
        )}
      </Stack>

      <Can action="devices_c">
        {isAddDeviceDialogOpen && (
          <AddDevice
            isOpen={isAddDeviceDialogOpen}
            onClose={handleCloseAddDevice}
          />
        )}
        {isImportDeviceDialogOpen && (
          <ImportDevices
            isOpen={isImportDeviceDialogOpen}
            setIsOpen={setIsImportDeviceDialogOpen}
          />
        )}
      </Can>

      <Can
        expression={() => !!selectedDevice}
        action="devices_d">
        <ConfirmWithCheckDialog
          icon={
            <DeleteRoundedIcon
              size={60}
              color="#ef5350"
            />
          }
          isOpen={isConfirmWithCheckOpen}
          onClose={handleCloseConfigWithCheck}
          onConfirmAction={handleConfirmAction}
          questionText={t('devices.deleteDevice.title')}
          subQuestionText={selectedDevice.name}
          copyText={selectedDevice.id}
          bodyText={t('devices.deleteDevice.subtitle')}
          inputSuggestionText={t('devices.deleteDevice.helper')}
        />
      </Can>
      {isSuperAdmin && selectedDevice && (
        <ConfirmWithCheckDialog
          icon={<DeleteForeverRoundedIcon fontSize="large" color="error" />}
          isOpen={isHardDeleteConfirmOpen}
          onClose={() => setIsHardDeleteConfirmOpen(false)}
          onConfirmAction={handleConfirmHardDelete}
          questionText={t('devices.hardDeleteDevice.title')}
          subQuestionText={selectedDevice.name}
          copyText={selectedDevice.id}
          bodyText={t('devices.hardDeleteDevice.subtitle')}
          inputSuggestionText={t('devices.hardDeleteDevice.helper')}
        />
      )}

      <Can action="devices_u">
        {isActionDialogOpen && (
          <Actions
            onSendAction={handleSendAction}
            isOpen={isActionDialogOpen}
            setIsOpen={handleSetIsOpenActionDialogOpen}
            device={selectedDevice}
          />
        )}
        {isEditDeviceDialogOpen && (
          <UpdateDevice
            deviceId={selectedDevice.id}
            isOpen={isEditDeviceDialogOpen}
            setIsOpen={handleSetIsOpenEditDevice}
          />
        )}
        {isSendActionOpen && (
          <SendAction
            device={selectedDevice}
            isOpen={isSendActionOpen}
            setIsOpen={handleSendActionIsOpen}
          />
        )}
      </Can>
    </>
  )
}

export default FilteredPublicList
